import { useQuery } from "@tanstack/react-query";
import { catalogEndpoints } from "../../api/endpoints/catalog";
import type { ApiError, Status } from "../../api/client/types/common.types";
import type {
  AdverseEventType,
  InterventionLevel,
  MaintenanceType,
  PoolRegulation,
  ReportType,
  EventMainFactor,
  EventSpecificFactor,
  AdverseEventInterventionLevel,
} from "../../api/client/types/catalog.types";


const queryKeysFactory = (rootKey: string) => ({
  root: [rootKey] as const,
  list: () => [rootKey, "list"] as const,
});

const statusQueryKeys = queryKeysFactory("statuses");
const reportTypeKeys = queryKeysFactory("reportTypes");
const poolRegulationKeys = queryKeysFactory("poolRegulations");
const maintenanceTypeKeys = queryKeysFactory("maintenanceTypes");
const adverseEventTypeKeys = queryKeysFactory("adverseEventTypes");
const interventionLevelKeys = queryKeysFactory("interventionLevels");
const eventMainFactorKeys = queryKeysFactory("eventMainFactors");

// Filtered keys include the filter param so React Query caches each result separately
const eventSpecificFactorKeys = {
  list: (mainFactorId: number) =>
    ["eventSpecificFactors", "list", mainFactorId] as const,
};
const adverseEventInterventionLevelKeys = {
  list: (adverseEventsTypeId: number) =>
    ["adverseEventInterventionLevels", "list", adverseEventsTypeId] as const,
};

export const useGetStatuses = () =>
  useQuery<Status[], ApiError>({
    queryKey: statusQueryKeys.list(),
    queryFn: () => catalogEndpoints.getStatuses(),
  });

export const useGetReportTypes = () =>
  useQuery<ReportType[], ApiError>({
    queryKey: reportTypeKeys.list(),
    queryFn: () => catalogEndpoints.getReportTypes(),
  });

export const useGetPoolRegulations = () =>
  useQuery<PoolRegulation[], ApiError>({
    queryKey: poolRegulationKeys.list(),
    queryFn: () => catalogEndpoints.getPoolRegulations(),
  });

export const useGetMaintenanceTypes = () =>
  useQuery<MaintenanceType[], ApiError>({
    queryKey: maintenanceTypeKeys.list(),
    queryFn: () => catalogEndpoints.getMaintenanceTypes(),
  });

export const useGetAdverseEventTypes = () =>
  useQuery<AdverseEventType[], ApiError>({
    queryKey: adverseEventTypeKeys.list(),
    queryFn: () => catalogEndpoints.getAdverseEventTypes(),
  });

export const useGetInterventionLevels = () =>
  useQuery<InterventionLevel[], ApiError>({
    queryKey: interventionLevelKeys.list(),
    queryFn: () => catalogEndpoints.getInterventionLevels(),
  });

export const useGetEventMainFactors = () =>
  useQuery<EventMainFactor[], ApiError>({
    queryKey: eventMainFactorKeys.list(),
    queryFn: () => catalogEndpoints.getEventMainFactors(),
    staleTime: 1000 * 60 * 5,
  });

export const useGetEventSpecificFactors = (mainFactorId: number | null) =>
  useQuery<EventSpecificFactor[], ApiError>({
    queryKey: mainFactorId ? eventSpecificFactorKeys.list(mainFactorId) : [],
    queryFn: () => catalogEndpoints.getEventSpecificFactors(mainFactorId!),
    enabled: !!mainFactorId,
    staleTime: 1000 * 60 * 5,
  });

export const useGetAdverseEventInterventionLevels = (
  adverseEventsTypeId: number | null
) =>
  useQuery<AdverseEventInterventionLevel[], ApiError>({
    queryKey: adverseEventsTypeId
      ? adverseEventInterventionLevelKeys.list(adverseEventsTypeId)
      : [],
    queryFn: () =>
      catalogEndpoints.getAdverseEventInterventionLevels(adverseEventsTypeId!),
    enabled: !!adverseEventsTypeId,
    staleTime: 1000 * 60 * 5,
  });
